import { Author, type AgentMessage } from '@diagon-alley/common';
import { Sparkle } from '@phosphor-icons/react';
import { Box } from '@mantine/core';
import React from 'react';
import StreamingText from '../StreamingText';

interface MessageProps {
    message: AgentMessage;
    isLast?: boolean;
    isStreaming?: boolean;
    showAnchor?: boolean;
}

const userBubbleStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.07) 100%)',
    color: '#3730a3',
    border: '1px solid rgba(99, 102, 241, 0.18)',
    borderRadius: '16px 16px 4px 16px',
    padding: '9px 14px',
    maxWidth: 'min(75%, 520px)',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontSize: '13.5px',
    lineHeight: 1.55,
};

const assistantCardStyle: React.CSSProperties = {
    borderLeft: '2px solid rgba(99, 102, 241, 0.3)',
    paddingLeft: 14,
    paddingTop: 1,
    paddingBottom: 1,
    maxWidth: 'min(90%, 680px)',
};

const Message: React.FC<MessageProps> = ({
    message,
    isLast = false,
    isStreaming = false,
    showAnchor = false,
}) => {
    const isUser = message.author === Author.USER;
    const shouldStream = !isUser && isStreaming && isLast;

    if (message.content.type === 'text') {
        return (
            <Box className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
                {isUser ? (
                    <div style={userBubbleStyle}>
                        {message.content.value}
                    </div>
                ) : (
                    <div style={assistantCardStyle}>
                        {showAnchor && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                                <Sparkle size={10} weight="fill" color="#6366f1" />
                                <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#6366f1',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                }}>
                                    Reparo AI
                                </span>
                            </div>
                        )}
                        <StreamingText
                            text={message.content.value}
                            shouldStream={shouldStream}
                        />
                    </div>
                )}
            </Box>
        );
    }

    if (message.content.type === 'error') {
        return (
            <Box className="flex w-full justify-start">
                <Box className="max-w-[80%] rounded-lg bg-red-600 p-3 text-white">
                    <span className="text-sm">Error: {message.content.value}</span>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="flex w-full justify-start">
            <Box className="max-w-[80%] rounded-lg bg-gray-700 p-3 text-gray-100">
                <span className="text-sm">
                    {JSON.stringify(message.content, null, 2)}
                </span>
            </Box>
        </Box>
    );
};

export default Message;
